/**
 * Ein Angebot im Einzelnen.
 *
 * Der Weg: Entwurf → versendet → angenommen oder abgelehnt. Ein
 * angenommenes Angebot wird in eine Rechnung umgewandelt — mit denselben
 * Positionen, aber als eigener Vorgang mit eigener Nummer. Das Angebot
 * bleibt bestehen und zeigt danach auf seine Rechnung.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dialog } from '../bausteine/Dialog';
import { Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datum, heute, zahl } from '../lib/format';
import { euro } from '../lib/geld';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { Angebot, Rechnung } from '../lib/typen';

export function AngebotDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigiere = useNavigate();
  const { darfSchreiben } = useSitzung();

  const abruf = useAbruf<Angebot>(id ? `/api/angebote/${id}` : null);
  const vorgang = useVorgang();

  const [ablehnenOffen, setzeAblehnenOffen] = useState(false);
  const [ablehnungsgrund, setzeAblehnungsgrund] = useState('');
  const [umwandelnOffen, setzeUmwandelnOffen] = useState(false);
  const [rechnungsdatum, setzeRechnungsdatum] = useState(heute());

  if (abruf.fehler) {
    return (
      <Seite titel="Angebot">
        <Fehlermeldung text={abruf.fehler} />
        <p style={{ marginTop: '1rem' }}>
          <Link to="/angebote">Zurück zur Angebotsliste</Link>
        </p>
      </Seite>
    );
  }

  if (!abruf.daten) {
    return (
      <Seite titel="Angebot">
        <Laedt />
      </Seite>
    );
  }

  const a = abruf.daten;
  const abgelaufen =
    a.status === 'versendet' && a.gueltigBis !== null && a.gueltigBis < heute();

  async function loese(pfad: string, rumpf?: unknown): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/angebote/${a.id}/${pfad}`, rumpf ?? {}),
    );
    if (ergebnis !== undefined) abruf.neuLaden();
  }

  async function verwerfen(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.entfernen(`/api/angebote/${a.id}`));
    if (ergebnis !== undefined) navigiere('/angebote');
  }

  async function umwandeln(): Promise<void> {
    // Die Antwort trägt die Rechnung in einem Feld, nicht als Rumpf: Sie
    // liefert zusätzlich einen Hinweistext mit.
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<{ rechnung: Rechnung; hinweis: string }>(
        `/api/angebote/${a.id}/in-rechnung`,
        { rechnungsdatum },
      ),
    );
    if (ergebnis !== undefined) {
      setzeUmwandelnOffen(false);
      // Direkt zur entstandenen Rechnung: Dort wird weitergearbeitet.
      navigiere(`/rechnungen/${ergebnis.rechnung.id}`);
    }
  }

  return (
    <Seite
      titel={a.nummer ?? 'Angebotsentwurf'}
      untertitel={a.betreff ?? undefined}
      aktionen={
        <>
          {a.status !== 'entwurf' ? (
            <a className="knopf" href={`/api/angebote/${a.id}/pdf`} target="_blank" rel="noreferrer">
              PDF
            </a>
          ) : null}
          {darfSchreiben && a.status === 'entwurf' ? (
            <>
              <button
                type="button"
                className="knopf"
                onClick={() => navigiere(`/angebote/${a.id}/aendern`)}
              >
                Ändern
              </button>
              <button
                type="button"
                className="knopf haupt"
                onClick={() => void loese('versenden')}
                disabled={vorgang.laeuft}
              >
                Versenden
              </button>
            </>
          ) : null}
          {darfSchreiben && a.status === 'versendet' ? (
            <>
              <button
                type="button"
                className="knopf haupt"
                onClick={() => void loese('annehmen')}
                disabled={vorgang.laeuft}
              >
                Angenommen
              </button>
              <button type="button" className="knopf gefahr" onClick={() => setzeAblehnenOffen(true)}>
                Abgelehnt
              </button>
            </>
          ) : null}
          {darfSchreiben && a.status === 'angenommen' && !a.rechnungId ? (
            <button type="button" className="knopf haupt" onClick={() => setzeUmwandelnOffen(true)}>
              In Rechnung umwandeln
            </button>
          ) : null}
          {/* Ein Angebot ist kein Buchhaltungsdokument — solange keine
              Rechnung daran hängt, darf es verschwinden. */}
          {darfSchreiben && !a.rechnungId ? (
            <button
              type="button"
              className="knopf gefahr"
              onClick={() => void verwerfen()}
              disabled={vorgang.laeuft}
            >
              Verwerfen
            </button>
          ) : null}
        </>
      }
    >
      <div className="stapel" style={{ maxWidth: '1100px' }}>
        <Fehlermeldung text={vorgang.fehler} />

        {a.status === 'entwurf' ? (
          <Meldung ton="info" titel="Entwurf.">
            Die Nummer entsteht beim Versenden. Ein Angebot trägt keinen
            Zahlungscode — es ist nichts zu zahlen.
          </Meldung>
        ) : null}

        {abgelaufen ? (
          <Meldung ton="warnung" titel="Abgelaufen.">
            Die Gültigkeit endete {datum(a.gueltigBis)}. Annehmen ist weiter
            möglich, sollte aber mit dem Kunden abgestimmt sein — die Preise
            sind älter als vereinbart.
          </Meldung>
        ) : null}

        {a.rechnungId ? (
          <Meldung ton="gut" titel="In eine Rechnung umgewandelt.">
            <Link to={`/rechnungen/${a.rechnungId}`}>Zur Rechnung</Link>
          </Meldung>
        ) : null}

        <div className="raster zwei">
          <div className="karte">
            <div className="karte-kopf">
              <h2>Angebot</h2>
              <Marker status={abgelaufen ? 'abgelaufen' : a.status} />
            </div>
            <div className="karte-koerper">
              <dl className="paare">
                <dt>Nummer</dt>
                <dd className="fest">{a.nummer ?? <span className="still">noch keine</span>}</dd>
                <dt>Datum</dt>
                <dd>{datum(a.angebotsdatum)}</dd>
                <dt>Gültig bis</dt>
                <dd>{datum(a.gueltigBis)}</dd>
                {a.leistungVon || a.leistungBis ? (
                  <>
                    <dt>Leistungszeitraum</dt>
                    <dd>
                      {datum(a.leistungVon)} – {datum(a.leistungBis)}
                    </dd>
                  </>
                ) : null}
                {a.versendetAm ? (
                  <>
                    <dt>Versendet</dt>
                    <dd>{datum(a.versendetAm)}</dd>
                  </>
                ) : null}
                {a.entschiedenAm ? (
                  <>
                    <dt>Entschieden</dt>
                    <dd>{datum(a.entschiedenAm)}</dd>
                  </>
                ) : null}
              </dl>
            </div>
          </div>

          <div className="karte">
            <div className="karte-kopf">
              <h2>Empfänger</h2>
            </div>
            <div className="karte-koerper">
              {a.kontakt ? (
                <address style={{ fontStyle: 'normal' }}>
                  {a.kontakt.name}
                  <br />
                  {a.kontakt.strasse}
                  <br />
                  {a.kontakt.plz} {a.kontakt.ort}
                </address>
              ) : (
                <span className="still">unbekannt</span>
              )}
            </div>
          </div>
        </div>

        <div className="karte">
          <div className="karte-kopf">
            <h2>Positionen</h2>
          </div>
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th className="schmal">#</th>
                  <th>Bezeichnung</th>
                  <th className="zahl schmal">Menge</th>
                  <th className="zahl schmal">Einzelpreis</th>
                  <th className="zahl">Summe</th>
                </tr>
              </thead>
              <tbody>
                {a.positionen.map((p) => (
                  <tr key={p.id ?? p.position}>
                    <td className="schmal still">{p.position}</td>
                    <td>
                      <span className="haupttext">{p.bezeichnung}</span>
                      {p.beschreibung ? <div className="nebentext">{p.beschreibung}</div> : null}
                    </td>
                    <td className="zahl schmal">
                      {zahl(p.menge, Number.isInteger(Number.parseFloat(p.menge)) ? 0 : 2)}{' '}
                      <span className="still klein">{p.einheit}</span>
                    </td>
                    <td className="zahl schmal">{euro(p.einzelpreis, false)}</td>
                    <td className="zahl">{euro(p.betragBrutto, false)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <strong>Gesamtbetrag</strong>
                  </td>
                  <td className="zahl">
                    <strong>{euro(a.betragBrutto)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {ablehnenOffen ? (
        <Dialog
          titel="Angebot abgelehnt"
          schliessen={() => setzeAblehnenOffen(false)}
          fuss={
            <>
              <button type="button" className="knopf" onClick={() => setzeAblehnenOffen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf gefahr"
                disabled={vorgang.laeuft}
                onClick={() => {
                  void loese('ablehnen', {
                    ...(ablehnungsgrund.trim() ? { grund: ablehnungsgrund.trim() } : {}),
                  }).then(() => setzeAblehnenOffen(false));
                }}
              >
                Als abgelehnt vermerken
              </button>
            </>
          }
        >
          <Textbereich
            beschriftung="Grund"
            wert={ablehnungsgrund}
            setzeWert={setzeAblehnungsgrund}
            zeilen={3}
            platzhalter="Zu teuer, anderer Anbieter, Projekt verschoben …"
            hinweis="Optional — aber beim nächsten Angebot an denselben Kunden nützlich."
          />
        </Dialog>
      ) : null}

      {umwandelnOffen ? (
        <Dialog
          titel="In eine Rechnung umwandeln"
          schliessen={() => setzeUmwandelnOffen(false)}
          fuss={
            <>
              <button type="button" className="knopf" onClick={() => setzeUmwandelnOffen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf haupt"
                disabled={vorgang.laeuft}
                onClick={() => void umwandeln()}
              >
                Rechnungsentwurf erzeugen
              </button>
            </>
          }
        >
          <div className="stapel">
            <Meldung ton="info">
              Es entsteht ein Rechnungsentwurf mit denselben Positionen. Die
              Rechnungsnummer wird erst beim Stellen vergeben, das Angebot
              bleibt unverändert erhalten.
            </Meldung>
            <Textfeld
              beschriftung="Rechnungsdatum"
              art="date"
              wert={rechnungsdatum}
              setzeWert={setzeRechnungsdatum}
              pflicht
            />
          </div>
        </Dialog>
      ) : null}
    </Seite>
  );
}
